import React, { useEffect, type ReactNode } from 'react';
import {
  isStaleChunkError,
  attemptStaleChunkReload,
  clearStaleChunkReloadCounter,
} from '@/utils/stale-chunk-reload';
import { notify } from '@/utils/notification-bridge';
import { extractApolloError } from '@/utils/graphql-error-codes';

interface GlobalErrorHandlerProps {
  children: ReactNode;
  onError?: (error: Error | string) => void;
}

/**
 * Catch-all for uncaught `window` errors and unhandled promise rejections that no component or
 * link handled. It surfaces them through the shared snackbar (`notify`) — a dismissible notice,
 * never a full-screen error — and is deliberately quiet about network/offline blips.
 *
 * GraphQL/network errors raised by Apollo are owned by the error link (see `apollo-client.ts`),
 * so we skip them here; React render crashes are owned by the `ErrorBoundary`.
 */
const GlobalErrorHandler: React.FC<GlobalErrorHandlerProps> = ({ children, onError }) => {
  // App booted successfully — clear any stale-chunk reload counter.
  useEffect(() => {
    clearStaleChunkReloadCounter();
  }, []);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('Global Error:', event.error);

      const staleChunkSource = event.error instanceof Error ? event.error : event.message;
      if (staleChunkSource && isStaleChunkError(staleChunkSource)) {
        if (attemptStaleChunkReload()) return;
        notify({ visibility: true, message: 'errors.graphql.appOutdated' });
        return;
      }

      // `window.onerror` events carry no `extensions`, so this is the only layer where matching
      // on `error.message` is acceptable — anywhere with a GraphQL response, branch on the code.
      if (event.message?.includes('NetworkError') || event.message?.includes('Failed to fetch')) {
        notify({ visibility: true, message: 'errors.graphql.network' });
      } else {
        notify({ visibility: true, message: 'errors.graphql.unexpected' });
      }

      onError?.(event.error || event.message);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled Promise Rejection:', event.reason);

      const reason = event.reason;
      const reasonMessage = reason?.message || String(reason || '');
      if (reason?.name === 'ChunkLoadError' || isStaleChunkError(reasonMessage)) {
        if (attemptStaleChunkReload()) return;
        notify({ visibility: true, message: 'errors.graphql.appOutdated' });
        return;
      }

      // GraphQL/network errors are owned by the Apollo error link (and the call site).
      if (extractApolloError(reason)) {
        event.preventDefault();
        return;
      }

      notify({ visibility: true, message: 'errors.graphql.unexpected' });
      onError?.(event.reason);
      event.preventDefault();
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [onError]);

  return <>{children}</>;
};

export default GlobalErrorHandler;
